// src/contexts/TranslationContext.tsx

import { createContext, useContext, type ReactNode } from 'react'
import type { TranslationContextType, Locale, Translations } from '@/shared/contexts/TranslationContextTypes'
import { useTranslationState } from '@/shared/hooks/useTranslationState'
import { findValue, interpolate, traverseKeys } from '@/lib/i18n/translationUtils'

export type { Locale, Translations }
export type { TranslationContextType }

const TranslationContext = createContext<TranslationContextType | undefined>(undefined)

export function TranslationProvider({ children }: { children: ReactNode }) {
  const { locale, unitContext, translations, setLocale } = useTranslationState()

  const t = (key: string, params?: Record<string, string>): string => {
    const keys = key.split('.')

    let value =
      findValue(translations.common, keys) ??
      findValue(translations.units, keys) ??
      findValue(translations.assets, keys) ??
      findValue(translations.settings, keys) ??
      findValue(translations.billing, keys) ??
      findValue(translations.registrations, keys)

    if (value === undefined && keys[0] === 'billing') {
      value = findValue(translations.billing, keys.slice(1))
    }

    if (value === undefined && keys[0] === 'registrations') {
      value = findValue(translations.registrations, keys.slice(1))
    }

    if (value === undefined && keys[0] === 'units') {
      value = findValue(translations.units, keys.slice(1))
    }

    if (value === undefined && keys[0] === 'settings') {
      value = findValue(translations.settings, keys.slice(1))
    }

    if (value === undefined) {
      console.warn(`Translation key not found: ${key}`)
      return key
    }

    return typeof value === 'string' ? interpolate(value, params) : key
  }

  const tu = (key: string, params?: Record<string, string>): string => {
    let value = traverseKeys(translations.units?.[unitContext], key)

    if (value === undefined) {
      value = traverseKeys(translations.units, key)
    }

    return typeof value === 'string' ? interpolate(value, params) : key
  }

  const ta = (group: string, key: string, params?: Record<string, string>): string => {
    let value = traverseKeys(translations.assets?.[group], key)

    if (value === undefined && translations.assets?.groups?.[group]) {
      value = translations.assets.groups[group][key]
    }

    if (value === undefined) {
      value = traverseKeys(translations.assets, key)
    }

    return typeof value === 'string' ? interpolate(value, params) : key
  }

  return (
    <TranslationContext.Provider
      value={{
        locale,
        unitContext,
        translations,
        t,
        tu,
        ta,
        setLocale,
      }}
    >
      {children}
    </TranslationContext.Provider>
  )
}

export function useTranslation() {
  const context = useContext(TranslationContext)

  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider')
  }

  return context
}

export function useUnitTranslation() {
  const { tu, unitContext } = useTranslation()

  return { tu, unitContext }
}

export function useAssetTranslation(group?: string) {
  const { ta } = useTranslation()

  if (!group) {
    return {
      ta: () => {
        throw new Error('useAssetTranslation: group is required.')
      },
    }
  }

  return {
    ta: (key: string, params?: Record<string, string>) => ta(group, key, params),
    group,
  }
}